import { useState, useEffect, useCallback, useMemo } from "react";
import {
  formatPlAmount,
  chartYAxisLabelsForAmount,
  niceMaxAmount,
} from "../utils/formatPlAmount";
import type { TwelveMonthTrendData } from "./useTwelveMonthTrendTypes";
import {
  getClientOverviewBootstrapData,
  getClientOverviewBootstrapError,
  getClientOverviewBootstrapPromise,
} from "../state/clientOverviewBootstrapStore";
import { useClientOverviewBootstrap } from "./useClientOverviewBootstrap";

export type { TwelveMonthTrendData };

const STUB_INSIGHTS_REVENUE = {
  title: "Revenue vs expenses",
  body: "Revenue and expenses for completed months (current month excluded). Compare month-on-month using the chart.",
  sentiment: "neutral" as const,
};

const STUB_INSIGHTS_CASH = {
  title: "Cash flow trend",
  body: "Cash in (credits) and cash out (debits) by month from bank transactions. Completed months only; current month excluded.",
  sentiment: "neutral" as const,
  lastClosedLabel: "—",
  inAmount: null as number | null,
  outAmount: null as number | null,
  netAmount: null as number | null,
};

export type UseTwelveMonthTrendResult = TwelveMonthTrendData & {
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

export function useTwelveMonthTrend(): UseTwelveMonthTrendResult {
  useClientOverviewBootstrap();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TwelveMonthTrendData | null>(null);

  const fetchTrend = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let bootstrapEntity = getClientOverviewBootstrapData();
      if (!bootstrapEntity) {
        const bootstrapPromise = getClientOverviewBootstrapPromise();
        if (bootstrapPromise) {
          bootstrapEntity = await bootstrapPromise;
        }
      }

      const bootstrapError = getClientOverviewBootstrapError();
      if (!bootstrapEntity) {
        throw new Error(bootstrapError ?? "Overview bootstrap data unavailable");
      }

      const section = bootstrapEntity.sections.twelveMonthTrend;
      if (section.status !== "ok" || !section.data) {
        throw new Error(section.error ?? "Monthly trend unavailable");
      }
      const entity = section.data;

      const months = entity.months;
      const fromLabel =
        entity.fromMonth || (months[0]?.monthLabel ?? "");
      const toLabel =
        entity.toMonth || (months.length > 0 ? months[months.length - 1]?.monthLabel ?? "" : "");
      const rangePart =
        (entity.rangeLabel && entity.rangeLabel.trim()) ||
        (fromLabel && toLabel
          ? fromLabel === toLabel
            ? fromLabel
            : `${fromLabel} – ${toLabel}`
          : "");
      const sectionSubtext = rangePart
        ? `${rangePart} · Closed months only`
        : "";

      // Show month only (e.g. "Apr") — user already knows the year
      const monthLabels = months.map((m) =>
        (m.monthLabel.split(" ")[0] || m.monthLabel).trim(),
      );

      // Revenue vs Expenses + EBITDA (absolute amount) on a single combo chart.
      const revenueVsExpenses = {
        chart: {
          title: rangePart
            ? `Revenue vs Expenses · ${rangePart}`
            : "Revenue vs Expenses",
          data: months.map((m, i) => ({
            monthLabel: monthLabels[i] ?? m.monthLabel,
            revenue: m.revenue,
            expenses: m.expenses,
            ebitda: m.ebitda,
          })),
        },
        insights: STUB_INSIGHTS_REVENUE,
      };

      // Cash In / Out
      const cashMax = Math.max(
        ...months.flatMap((m) => [m.cashIn, m.cashOut]),
        1,
      );
      const cashScaleMax = niceMaxAmount(cashMax);
      const cashInOut = {
        title: "CASH IN & CASH OUT",
        chart: {
          yAxisLabels: chartYAxisLabelsForAmount(cashScaleMax),
          monthLabels,
          cashIn: months.map((m) => m.cashIn),
          cashOut: months.map((m) => m.cashOut),
          tooltips: months.map((m, i) => ({
            monthLabel: monthLabels[i] ?? "",
            cashIn: formatPlAmount(m.cashIn),
            cashOut: formatPlAmount(m.cashOut),
          })),
        },
        insights: STUB_INSIGHTS_CASH,
      };

      setData({
        sectionTitle: "Monthly trend",
        sectionSubtext,
        revenueVsExpenses,
        cashInOut,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load monthly trend";
      setError(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrend();
  }, [fetchTrend]);

  const defaultData = useMemo(
    (): TwelveMonthTrendData => ({
      sectionTitle: "Monthly trend",
      sectionSubtext: "",
      revenueVsExpenses: {
        chart: {
          title: "Revenue vs Expenses",
          data: [],
        },
        insights: STUB_INSIGHTS_REVENUE,
      },
      cashInOut: {
        title: "CASH IN & CASH OUT",
        chart: {
          yAxisLabels: [formatPlAmount(0)],
          monthLabels: [],
          cashIn: [],
          cashOut: [],
        },
        insights: STUB_INSIGHTS_CASH,
      },
    }),
    [],
  );

  return {
    ...(data ?? defaultData),
    loading,
    error,
    refetch: fetchTrend,
  };
}
